import React from 'react';
import PropTypes from 'prop-types';
import { useTable, usePagination, useSortBy, useFilters } from 'react-table';
import Button from 'react-bootstrap/Button';
import { FaAngleRight, FaAngleLeft } from 'react-icons/fa';

import { TableStyle } from './style';
import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';
import TextColumnFilter from './Filters/TextColumnFilter';

export default function Table({
  columns,
  data,
  className,
  loading,
  sortable,
  filterable,
}) {
  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: TextColumnFilter,
    }),
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    // pageOptions,
    pageCount,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      autoResetPage: false,
      autoResetFilters: false,
      autoResetSortBy: false,
      disableSortBy: !sortable,
      disableFilters: !filterable,
      defaultColumn,
    },
    useFilters,
    useSortBy,
    usePagination,
  );

  return (
    <TableStyle className={className} loading={loading} aria-busy={loading}>
      <table {...getTableProps()}>
        <thead>
          {
            // Loop over the header rows
            headerGroups.map(headerGroup => (
              // Apply the header row props
              <tr {...headerGroup.getHeaderGroupProps()}>
                {
                  // Loop over the headers in each row
                  headerGroup.headers.map(column => (
                    // Apply the header cell props
                    <th
                      {...column.getHeaderProps({
                        className: column.className,
                      })}
                    >
                      <div
                        className="column-header"
                        {...column.getSortByToggleProps()}
                      >
                        {
                          // Render the header
                          column.render('Header')
                        }
                        <span>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <MdArrowDropDown />
                            ) : (
                              <MdArrowDropUp />
                            )
                          ) : (
                            ''
                          )}
                        </span>
                      </div>
                      <div className="column-filter">
                        {column.canFilter ? column.render('Filter') : null}
                      </div>
                    </th>
                  ))
                }
              </tr>
            ))
          }
        </thead>
        {/* Apply the table body props */}
        <tbody {...getTableBodyProps()}>
          {
            // Loop over the current page rows
            page.map(row => {
              // Prepare the row for display
              prepareRow(row);
              return (
                // Apply the row props
                <tr {...row.getRowProps()}>
                  {
                    // Loop over the rows cells
                    row.cells.map(cell => (
                      // Apply the cell props
                      <td
                        {...cell.getCellProps({
                          className: `${cell.column.className} data`,
                        })}
                      >
                        {
                          // Render the cell contents
                          cell.render('Cell')
                        }
                      </td>
                    ))
                  }
                </tr>
              );
            })
          }
        </tbody>
      </table>
      {/* pagination component */}
      <div className="pagination">
        <span style={{ padding: '10px' }}>
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageCount}
          </strong>
        </span>
        <span>
          <Button
            variant="light"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            aria-label="previous-page"
          >
            <FaAngleLeft size="1.5rem" />
          </Button>
          <Button
            variant="light"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            aria-label="next-page"
          >
            <FaAngleRight size="1.5rem" />
          </Button>
        </span>
        <span style={{ padding: '10px' }}>
          <label style={{ marginRight: '5px' }} htmlFor="pageNumber">
            Go to page:{' '}
          </label>
          <input
            id="pageNumber"
            type="number"
            defaultValue={pageIndex + 1}
            onChange={e => {
              const pageInd = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(pageInd);
            }}
            style={{ width: '75px' }}
          />
        </span>
        <select
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value));
          }}
          style={{ margin: '10px' }}
        >
          {[5, 10, 20, 30, 40, 50].map(size => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </TableStyle>
  );
}

Table.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  className: PropTypes.string,
  loading: PropTypes.string,
  sortable: PropTypes.bool,
  filterable: PropTypes.bool,
};
